import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { renderMarkdown } from './markdownUtils';
import InsightsChat from './InsightsChat';

export default function InsightsPanel({ projectId, uptimeVThreshold = 10.0, uptimeIThreshold = 1.0 }) {
  const { user } = useAuth();
  const [insight, setInsight]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded]     = useState(false);

  const fetchInsight = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/projects/${projectId}/insights`);
      setInsight(res.data.content ? res.data : null);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchInsight(); }, [fetchInsight]);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/api/projects/${projectId}/insights/generate`, {
        v_threshold: uptimeVThreshold,
        i_threshold: uptimeIThreshold,
      });
      // Poll every 3s until new insight appears
      const start = Date.now();
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/api/projects/${projectId}/insights`);
          const fresh = res.data.content ? res.data : null;
          if (fresh && (!insight || fresh.generated_at > insight.generated_at)) {
            setInsight(fresh);
            setGenerating(false);
            clearInterval(poll);
          }
        } catch {}
        if (Date.now() - start > 90000) { setGenerating(false); clearInterval(poll); }
      }, 3000);
    } catch {
      setGenerating(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <span className="card-title">
          <span style={{ marginRight: 8 }}>🤖</span>
          AI Insights
          {insight
            ? <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 400, color: '#6b7a8d' }}>
                analyzed {insight.data_from?.slice(0,10)} → {insight.data_to?.slice(0,10)}
              </span>
            : <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 400, color: '#6b7a8d' }}>
                · analysis &amp; Q&amp;A
              </span>
          }
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {insight && (
            <span style={{ fontSize: 11, color: '#6b7a8d' }}>
              {new Date(insight.generated_at).toLocaleString()}
            </span>
          )}
          {user?.is_admin && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={e => { e.stopPropagation(); generate(); }}
              disabled={generating}
              style={{ fontSize: 12 }}
            >
              {generating ? '⏳ Analyzing…' : '↻ Refresh'}
            </button>
          )}
          <span style={{ color: '#6b7a8d', fontSize: 13 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '16px 20px', fontSize: 13, color: '#2c3e50', lineHeight: 1.6 }}>
          {loading && <div className="empty-state-text">Loading…</div>}
          {!loading && generating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#6b7a8d' }}>
              <div className="spinner" style={{ width: 18, height: 18 }} />
              Analyzing all project data — this takes about 20–30 seconds…
            </div>
          )}
          {!loading && !generating && !insight && (
            <div className="empty-state">
              <div className="empty-state-text">No analysis yet.</div>
              {user?.is_admin && (
                <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={generate}>
                  Generate first analysis
                </button>
              )}
            </div>
          )}
          {!loading && !generating && insight && (
            <div>
              {/* Priority window banner */}
              {insight.recent_from && insight.recent_to && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: '#f0f4fb',
                  border: '1px solid #d0daf0',
                  borderRadius: 7,
                  padding: '7px 13px',
                  marginBottom: 14,
                  fontSize: 12,
                  color: '#3a4f7a',
                }}>
                  <span style={{ fontSize: 14 }}>📅</span>
                  <span>
                    Analysis prioritises the most recent data available, with highest weight on the last few weeks.
                    {' '}<strong>Priority window:</strong>{' '}
                    {new Date(insight.recent_from + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' → '}
                    {new Date(insight.recent_to + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              )}
              {insight.stale && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'rgba(230,130,0,0.10)',
                  border: '1px solid rgba(230,130,0,0.35)',
                  borderRadius: 8,
                  padding: '9px 14px',
                  marginBottom: 14,
                  fontSize: 12,
                  color: '#7a4500',
                }}>
                  <span style={{ fontSize: 15 }}>📂</span>
                  <span style={{ flex: 1 }}>
                    New plant data has been uploaded since this analysis was generated.
                  </span>
                  {user?.is_admin && (
                    <button
                      className="btn btn-sm"
                      style={{
                        background: 'rgba(230,130,0,0.18)',
                        border: '1px solid rgba(230,130,0,0.4)',
                        color: '#7a4500',
                        fontWeight: 600,
                        padding: '3px 10px',
                        cursor: 'pointer',
                        borderRadius: 5,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                      }}
                      onClick={e => { e.stopPropagation(); generate(); }}
                      disabled={generating}
                    >
                      Re-analyze now
                    </button>
                  )}
                </div>
              )}
              {renderMarkdown(insight.content)}

              {/* Contributor attribution */}
              <div style={{
                marginTop: 18,
                paddingTop: 14,
                borderTop: '1px solid #e8edf4',
                fontSize: 11,
                color: '#6b7a8d',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 6,
              }}>
                <span style={{ fontWeight: 600, marginRight: 4 }}>🧠 Training contributors:</span>
                {insight.contributors?.length > 0
                  ? insight.contributors.map(c => (
                      <span
                        key={c.email}
                        title={c.email}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: '#eef2fb',
                          border: '1px solid #d0dae8',
                          borderRadius: 12,
                          padding: '2px 9px',
                          fontWeight: 600,
                          color: '#3a4f7a',
                          fontSize: 11,
                          cursor: 'default',
                        }}
                      >
                        {c.name}
                        <span style={{ color: '#8892a0', fontWeight: 400 }}>{c.pct.toFixed(0)}%</span>
                      </span>
                    ))
                  : <span style={{ fontStyle: 'italic', color: '#9aa3af' }}>
                      Not tracked yet — log field events or answer questions in chat to start seeing contributions here.
                    </span>
                }
              </div>
            </div>
          )}

          {/* Q&A chat — always shown when expanded (even without a prior analysis) */}
          {!loading && (
            <InsightsChat projectId={projectId} />
          )}
        </div>
      )}
    </div>
  );
}
